import { useEffect, useMemo, useState, useRef } from 'react';
import { useAppState } from 'context/AppState';
import { useTranslation } from 'react-i18next';
import { colors, Skeleton } from 'syngenta-digital-cropwise-react-ui-kit';
import { Text, StyledSpinner } from '../../V2Common.styles';
import {
  SectionElement,
  SectionElementContainer,
  StyledTitle,
  FullScreenChartContainerStyled,
  StyledIconRotatePhone,
} from './ProductPerformance.styles';
import { ProductPerformanceChart, ProductData } from './ProductPerformanceChart';
import track from 'utils/amplitudeWrapper';
import { useBreakpoint } from 'hooks';
import { Tpr } from 'base/types/RecommendationCropwise';
import { BarChartLabelProps } from 'components/BarChart/BarChartLabel';
import { labels, productPerformanceDisplayCategoryBySource } from './ChartLabels';

interface ProductPerformanceProps {
  sectionRef?: any;
  landscapeAllowed?: boolean;
  chartInfo?: Tpr | null;
}

type ReadonlyProductPerformanceProps = Readonly<ProductPerformanceProps>;

function ProductPerformance({
  sectionRef,
  landscapeAllowed,
  chartInfo,
}: ReadonlyProductPerformanceProps): JSX.Element {
  const { landscape, isMobile } = useBreakpoint();
  const [t] = useTranslation();
  const [activeProduct, setActiveProduct] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [environmentalStressData, setEnvironmentalStressData] = useState<BarChartLabelProps[]>([]);

  const {
    apiData: { recommendationCropwise, breakoutsGlobal },
  } = useAppState();

  const recommendation =
    recommendationCropwise?.recommendations[0].multi_field.config.recommendation;
  const recommendedProducts = recommendationCropwise?.recommendations[0].products;

  // Environmental Stress Data
  useEffect(() => {
    if (chartInfo) {
      const data = [
        {
          value: chartInfo?.VLOW?.Percentage || 0,
          title: t('Wet & Cold'),
          temperatureLabel: t('Very Low Temperature'),
          precipitationLabel: t('High Precipitation'),
          barColor: colors.blue60,
          titleColor: colors.blue60,
          years: chartInfo?.VLOW?.Years || [],
          Category: 'VL',
        },
        {
          value: chartInfo?.LOW?.Percentage || 0,
          title: t('Cool & Rainy'),
          temperatureLabel: t('Low Temperature'),
          precipitationLabel: t('High Precipitation'),
          barColor: colors.blue30,
          titleColor: colors.blue30,
          years: chartInfo?.LOW?.Years || [],
          Category: 'L',
        },
        {
          value: chartInfo?.MED?.Percentage || 0,
          title: t('Mild'),
          temperatureLabel: t('Medium Temperature'),
          precipitationLabel: t('Medium Precipitation'),
          barColor: colors.yellow30,
          titleColor: colors.yellow30,
          years: chartInfo?.MED?.Years || [],
          Category: 'M',
        },
        {
          value: chartInfo?.HIGH?.Percentage || 0,
          title: t('Warm & Dry'),
          temperatureLabel: t('High Temperature'),
          precipitationLabel: t('Low Precipitation'),
          barColor: colors.red30,
          titleColor: colors.red30,
          years: chartInfo?.HIGH?.Years || [],
          Category: 'H',
        },
        {
          value: chartInfo?.VHIGH?.Percentage || 0,
          title: t('Very Hot & Dry'),
          temperatureLabel: t('Very High Temperature'),
          precipitationLabel: t('Low Precipitation'),
          barColor: colors.red60,
          titleColor: colors.red60,
          years: chartInfo?.VHIGH?.Years || [],
          Category: 'VH',
        },
      ].filter((item) => item.value > 0);

      setEnvironmentalStressData(data);
    }
  }, [chartInfo, t]);

  // Stats Data
  const statsData = useMemo(() => {
    const allProductsStats: Record<string, ProductData[]> = {};
    const availableCategories = environmentalStressData?.map((item) => item.Category);

    const productColors = (recommendedProducts || []).reduce(
      (acc: Record<string, string>, product) => {
        acc[product.product_name] = product.color;
        return acc;
      },
      {} as Record<string, string>
    );

    Object.keys(breakoutsGlobal).forEach((product) => {
      const productStats =
        breakoutsGlobal[product]?.breakouts
          ?.find((item) => item.name === 'Temperature/Precipitation Ratio')
          ?.stats?.reduce<ProductData[]>((accumulator, item) => {
            if (!item.CATEGORY) {
              return accumulator;
            }

            const displayCategory =
              productPerformanceDisplayCategoryBySource[
                item.CATEGORY as keyof typeof productPerformanceDisplayCategoryBySource
              ] ?? item.CATEGORY;

            if (!availableCategories.includes(displayCategory)) {
              return accumulator;
            }

            accumulator.push({
              ...item,
              CATEGORY: displayCategory,
              value: item.WIN_PRCT,
              barColor: productColors[product] || colors.blue60,
              color: productColors[product] || colors.blue60,
              titleColor: colors.neutral90,
              ...labels[displayCategory as keyof typeof labels],
            });

            return accumulator;
          }, []) ?? [];

      if (productStats.length) {
        allProductsStats[product] = productStats;
      }
    });

    return allProductsStats;
  }, [breakoutsGlobal, environmentalStressData, recommendedProducts]);

  // Chart scaling values
  const values = useMemo(() => {
    const minValue: any[] = [];
    Object.values(statsData).forEach((productStats) => {
      productStats.forEach((item) => {
        minValue.push(item.value);
      });
    });
    return minValue.length ? minValue : [0, 1];
  }, [statsData]);
  const min = useMemo(() => Math.floor(Math.min(...values)), [values]);

  useEffect(() => {
    if (!recommendation) {
      return;
    }
    if (activeProduct === '') {
      const [firstProduct] = recommendedProducts || [];
      setActiveProduct(firstProduct?.product_name);
    }
  }, [recommendation, activeProduct, recommendedProducts]);

  useEffect(() => {
    if (!sectionRef?.current) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            track('ProductPerformanceChart_Scrolled', {
              timestamp: new Date().toISOString(),
              productsCount: recommendedProducts?.length || 0,
              categoriesCount: environmentalStressData?.length || 0,
            });
          }
        });
      },
      { threshold: 0.3 }
    );

    observer.observe(sectionRef.current);

    return () => {
      observer.disconnect();
    };
  }, [sectionRef, recommendedProducts, environmentalStressData]);

  const SkeletonLoader = useMemo(() => {
    return (
      <Skeleton
        active
        style={{ padding: '100px 64px 0px 64px' }}
        paragraph={{ rows: 4, width: '100%' }}
      />
    );
  }, []);

  return (
    <div ref={sectionRef}>
      <SectionElementContainer data-testid="product-performance-section">
        <SectionElement>
          <StyledTitle>{t('Product Performance')}</StyledTitle>
          <Text>{t('productperformanceDescription')}</Text>

          {!recommendedProducts?.length || Object.keys(statsData).length === 0 ? (
            SkeletonLoader
          ) : (
            <FullScreenChartContainerStyled fullScreen={landscapeAllowed}>
              {landscape && isMobile && (
                <StyledIconRotatePhone height={20} color={colors.neutral60} width={20} />
              )}

              <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                <br />
              </div>
              <ProductPerformanceChart
                yMin={min}
                data={statsData || []}
                environmentalStressData={environmentalStressData}
                recommendedProducts={recommendedProducts}
              />
            </FullScreenChartContainerStyled>
          )}
        </SectionElement>
      </SectionElementContainer>
      {isLoading && <StyledSpinner />}
    </div>
  );
}

export default ProductPerformance;
